/*
 * KOJI CHAT — motore condiviso
 *
 * Usato da:
 *   - sections/koji-general-chat.liquid  (La chat di Koji, in home)
 *   - sections/koji-comments.liquid      (Commenti nella pagina collezione)
 *   - sections/koji-chat-hub.liquid      (pagina Chat: stanze, cambio stanza senza ricaricare)
 * Stile: assets/koji-chat.css + assets/koji-chat-v3.css
 *
 * Cosa fa:
 *   - parte solo quando la sezione sta per entrare nello schermo (nessuna lettura Firebase per chi non scrolla fin qui)
 *   - legge gli ultimi N messaggi della stanza (paginazione con "Carica altri")
 *   - aggiorna solo i messaggi cambiati (niente ridisegno totale, niente sfarfallio)
 *   - spoiler a tocco, badge staff, segnalazione, blocco link, limite di invio, avvisi senza alert()/confirm()
 *   - "In tendenza" (solo home): legge la collezione "rooms" (una riga per collezione) ordinata per numero di messaggi, mai più 1000 messaggi
 *
 * Firebase: window.kojiDb arriva da snippets/firebase-init.liquid.
 * Campi dei messaggi (collezione "comments"): NON aggiungerne di nuovi senza aggiornare le regole di Firestore.
 */

const FIRESTORE_URL = 'https://www.gstatic.com/firebasejs/10.12.2/firebase-firestore.js';
const AUTH_URL = 'https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js';
const MAX_LEN = 500;
const SEND_MIN_GAP_MS = 4000;
const SEND_MAX_PER_MIN = 6;
const AVATAR_HOSTS = ['cdn.shopify.com', 'kojimanga.com', 'www.kojimanga.com'];
const LINK_TLDS = ['com', 'it', 'net', 'org', 'io', 'me', 'ly', 'gg', 'tv', 'xyz', 'shop', 'store', 'info', 'co', 'eu', 'ru', 'cn', 'top', 'click', 'link', 'app', 'dev', 'site', 'online', 'live', 'club', 'page', 'to', 'cc', 'ws', 'vip', 'biz'];
const LINK_REGEX = new RegExp('(^|[^a-z0-9-])[a-z0-9-]+\\.(' + LINK_TLDS.join('|') + ')($|[^a-z0-9])');

const DEFAULT_STRINGS = {
  t0: '0 Messaggi',
  t1: '1 Messaggio',
  tx: '[count] Messaggi',
  just_now: 'Proprio ora',
  anonymous: 'Otaku Anonimo',
  btn_reply: 'Rispondi',
  btn_delete: 'Elimina',
  btn_report: 'Segnala',
  login_cta: 'Accedi',
  alert_login_reply: "Fai l'accesso su Koji per rispondere ai messaggi degli altri!",
  alert_login_like: "Fai l'accesso per lasciare un mi piace!",
  confirm_delete: 'Vuoi davvero cancellare questo messaggio?',
  delete_title: 'Eliminare il messaggio?',
  cancel: 'Annulla',
  error_delete: "Ops! C'è stato un piccolo errore. Riprova tra un attimo.",
  error_delete_denied: 'Questo messaggio non si può eliminare da qui (è stato scritto da un altro dispositivo). Scrivi allo staff se serve.',
  error_send: 'Il messaggio non è partito! Controlla la tua connessione e riprova.',
  error_like: 'Non sono riuscito a registrare il mi piace. Riprova tra un attimo.',
  rate_limit: 'Piano con i messaggi! Aspetta qualche secondo prima di scriverne un altro.',
  no_links: 'Niente link nei messaggi: è la regola 3. Scrivi senza indirizzi web.',
  toast_deleted: 'Messaggio eliminato.',
  toast_reported: 'Grazie, abbiamo ricevuto la segnalazione.',
  toast_reported_already: 'Hai già segnalato questo messaggio.',
  badge_last: 'Ultimo messaggio',
  badge_empty: 'Nessun messaggio: inizia tu',
  ghost_deleted: 'Messaggio eliminato',
  ghost_old: 'In risposta a un messaggio precedente',
  spoiler: 'Spoiler · tocca per rivelare',
  staff: 'Staff',
  like_aria: 'Mi piace',
  options_aria: 'Opzioni',
  expand: 'Apri tutta la conversazione ([count])',
  live_new: 'Nuovo messaggio da [nome]',
  report_subject: 'Segnalazione messaggio',
  trend_one: 'messaggio',
  trend_other: 'messaggi',
  today: 'Oggi',
  yesterday: 'Ieri',
  peek_empty: 'Nessun messaggio: scrivi tu il primo',
  spoiler_hidden: 'Spoiler nascosto',
  new_messages: 'Nuovi messaggi',
  emoji_aria: 'Inserisci una emoji',
  spoiler_aria: 'Nascondi il messaggio come spoiler',
  in_reply_to: 'in risposta a',
  sending: 'Invio…',
  retry: 'Riprova',
  goto_missing: 'Il messaggio originale è più indietro nella conversazione: carica altri messaggi per vederlo.',
  logged_back: 'Accesso effettuato: ora puoi scrivere!',
  spoiler_reply: 'Spoiler nascosto'
};

const EMOJIS = ['😂', '😭', '🔥', '❤️', '👏', '😍', '🤯', '😱', '🥲', '👀', '💀', '🙏', '😎', '🤔', '✨', '💜', '👍', '😅', '🥹', '😤', '📚', '🎌', '⚔️', '🛒'];

const ICONS = {
  heart: "<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><path d='M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z'></path></svg>",
  reply: "<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><polyline points='15 10 20 15 15 20'></polyline><path d='M4 4v7a4 4 0 0 0 4 4h12'></path></svg>",
  more: "<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><circle cx='12' cy='12' r='1'></circle><circle cx='19' cy='12' r='1'></circle><circle cx='5' cy='12' r='1'></circle></svg>",
  trash: "<svg viewBox='0 0 24 24' width='14' height='14' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><polyline points='3 6 5 6 21 6'></polyline><path d='M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2'></path></svg>",
  flag: "<svg viewBox='0 0 24 24' width='14' height='14' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><path d='M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z'></path><line x1='4' y1='22' x2='4' y2='15'></line></svg>"
};

/* ---------- Utilità pure (esportate per i test) ---------- */

export function escapeHtml(value) {
  return String(value == null ? '' : value).replace(/[&<>"'`]/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' }[c];
  });
}

/* I nomi salvati in passato potevano contenere entità già codificate (es. &#39;): si decodificano UNA volta e poi si ricodificano */
export function decodeBasicEntities(value) {
  return String(value == null ? '' : value).replace(/&(amp|lt|gt|quot|#39);/g, function (m, g) {
    return { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'" }[g];
  });
}

/* Via i caratteri di controllo, mai più di 3 a capo di fila */
export function cleanMessage(value) {
  return String(value || '')
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim();
}

/* Regola 3: niente link. Controllo lato browser, serve a evitare gli errori in buona fede */
export function hasLink(text) {
  const t = String(text || '').toLowerCase();
  if (t.indexOf('http://') !== -1 || t.indexOf('https://') !== -1 || t.indexOf('www.') !== -1) return true;
  return LINK_REGEX.test(t);
}

export function relTime(ts, justNow, now) {
  const diff = (now || Date.now()) - ts;
  if (diff < 45000) return justNow;
  const m = Math.round(diff / 60000);
  if (m < 60) return m + ' min fa';
  const h = Math.floor(m / 60);
  if (h < 24) return h + (h === 1 ? ' ora fa' : ' ore fa');
  const d = Math.floor(h / 24);
  if (d < 7) return d + (d === 1 ? ' giorno fa' : ' giorni fa');
  const date = new Date(ts);
  const sameYear = date.getFullYear() === new Date(now || Date.now()).getFullYear();
  return date.toLocaleDateString('it-IT', sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
}

function fullDate(ts) {
  return new Date(ts).toLocaleString('it-IT', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function dayKey(ts) {
  const d = new Date(ts || Date.now());
  return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
}

function dayLabel(ts, S) {
  const t = ts || Date.now();
  const now = Date.now();
  if (dayKey(t) === dayKey(now)) return S.today;
  if (dayKey(t) === dayKey(now - 86400000)) return S.yesterday;
  const sameYear = new Date(t).getFullYear() === new Date(now).getFullYear();
  return new Date(t).toLocaleDateString('it-IT', sameYear ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' });
}

function msOf(d) {
  const t = d && d.timestamp;
  return t && typeof t.toMillis === 'function' ? t.toMillis() : 0;
}

/* Gli avatar sono accettati solo se sono https e arrivano dal sito o dalla CDN di Shopify */
function safeAvatarUrl(value) {
  if (!value || typeof value !== 'string' || value.length > 600) return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.protocol !== 'https:') return null;
    if (AVATAR_HOSTS.concat(window.location.hostname).indexOf(url.hostname) === -1) return null;
    return url.href;
  } catch (e) {
    return null;
  }
}

function prettifyHandle(handle) {
  return String(handle || '').replace(/-/g, ' ');
}

/* ---------- Dialog di conferma, toast: uno solo per pagina ---------- */

let confirmEl = null;
let toastEl = null;
let toastTimer = null;

const REDUCED = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

/* chiusura animata: prima l'uscita, poi close() */
function closeDialog(d, returnValue) {
  if (!d.open || d.classList.contains('is-closing')) return;
  const finish = function () {
    if (!d.classList.contains('is-closing')) return;
    d.classList.remove('is-closing');
    if (returnValue !== undefined) d.returnValue = returnValue;
    d.close();
  };
  if (REDUCED) { d.classList.add('is-closing'); finish(); return; }
  d.classList.add('is-closing');
  d.addEventListener('animationend', finish, { once: true });
  setTimeout(finish, 450);
}

function openDialog(dialog) {
  if (!dialog.getAttribute('data-kc-anim')) {
    dialog.setAttribute('data-kc-anim', '1');
    dialog.addEventListener('cancel', function (e) { e.preventDefault(); closeDialog(dialog); });
    dialog.addEventListener('submit', function (e) {
      e.preventDefault();
      closeDialog(dialog, e.submitter && e.submitter.value != null ? e.submitter.value : '');
    });
  }
  dialog.classList.remove('is-closing');
  if (typeof dialog.showModal === 'function') dialog.showModal();
  else dialog.setAttribute('open', '');
}

function askConfirm(o) {
  return new Promise(function (resolve) {
    if (!confirmEl) {
      confirmEl = document.createElement('dialog');
      confirmEl.className = 'kc-dialog kc-dialog--confirm';
      document.body.appendChild(confirmEl);
    }
    confirmEl.innerHTML =
      "<form method='dialog' class='kc-dialog-body'>" +
      "<h4 class='kc-dialog-title'>" + escapeHtml(o.title) + '</h4>' +
      "<p class='kc-dialog-text'>" + escapeHtml(o.text) + '</p>' +
      "<div class='kc-dialog-actions'>" +
      "<button value='cancel' class='kc-btn kc-btn--ghost' autofocus>" + escapeHtml(o.cancel) + '</button>' +
      "<button value='ok' class='kc-btn" + (o.danger ? ' kc-btn--danger' : '') + "'>" + escapeHtml(o.ok) + '</button>' +
      '</div></form>';
    confirmEl.returnValue = '';
    confirmEl.onclose = function () { resolve(confirmEl.returnValue === 'ok'); };
    openDialog(confirmEl);
  });
}

function toast(message, opts) {
  const o = opts || {};
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'kc-toast';
    toastEl.setAttribute('role', 'status');
    document.body.appendChild(toastEl);
  }
  toastEl.innerHTML = '<span>' + escapeHtml(message) + '</span>' +
    (o.href ? " <a href='" + escapeHtml(o.href) + "'>" + escapeHtml(o.label || '') + '</a>' : '') +
    (o.action ? " <button type='button' class='kc-toast-act'>" + escapeHtml(o.action.label) + '</button>' : '');
  if (o.action) {
    toastEl.querySelector('.kc-toast-act').addEventListener('click', function () { toastEl.classList.remove('is-visible'); o.action.fn(); });
  }
  toastEl.className = 'kc-toast is-visible' + (o.kind ? ' is-' + o.kind : '');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, o.href || o.action ? 8000 : 4500);
}

/* ---------- Chat ---------- */

function initChat(root) {
  const cfgEl = root.querySelector('script[data-kc-config]');
  if (!cfgEl) return;
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { console.error('[koji-chat] configurazione non valida', e); return; }

  const S = Object.assign({}, DEFAULT_STRINGS);
  Object.keys(cfg.strings || {}).forEach(function (k) {
    const v = cfg.strings[k];
    if (v !== '' && v != null) S[k] = v;
  });

  const user = cfg.user || {};
  const loggedIn = !!user.loggedIn;
  const userId = loggedIn ? String(user.id) : '';
  const hub = !!cfg.hub;
  const homeRoom = String(cfg.roomId == null ? '' : cfg.roomId);
  let roomId = homeRoom;
  let legacyRooms = Array.isArray(cfg.legacyRooms) ? cfg.legacyRooms.map(String) : [];
  const pageSize = Math.max(10, parseInt(cfg.pageSize, 10) || 30);
  const collapseN = parseInt(cfg.collapseMobile, 10) || 0;
  const staffIds = (cfg.staffIds || []).map(String).filter(Boolean);
  const isStaffMe = loggedIn && staffIds.indexOf(userId) !== -1;
  const loginUrl = cfg.loginUrl || '/account/login';
  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const stackedQuery = window.matchMedia ? window.matchMedia('(max-width: 989px)') : { matches: false };
  /* nella pagina della chat non esiste il pannello a schermo intero: la conversazione è già la pagina */
  const sheetQuery = hub ? { matches: false } : (window.matchMedia ? window.matchMedia('(max-width: 768px)') : { matches: false });

  const q = function (name) { return root.querySelector('[data-kc="' + name + '"]'); };
  const el = {
    list: q('list'), loading: q('loading'), empty: q('empty'), error: q('error'), retry: q('retry'),
    count: q('count'), badge: q('badge'), badgeText: q('badge-text'), badgeDot: q('badge-dot'),
    textarea: q('textarea'), submit: q('submit'), counter: q('counter'), composer: q('composer'),
    replyCtx: q('reply-ctx'), replyName: q('reply-name'), replyCancel: q('reply-cancel'),
    more: q('more'), expand: q('expand'), live: q('live'), scroll: q('scroll'),
    sort: q('sort'), rulesBtn: q('rules-btn'), rulesDialog: q('rules-dialog'),
    inputImg: q('input-img'), inputInitial: q('input-initial'),
    trending: q('trending'), trendingList: q('trending-list'),
    peek: q('peek'), peekText: q('peek-text'), peekMeta: q('peek-meta'), sheetClose: q('sheet-close'), sheetOpen: q('sheet-open'), feed: q('feed'),
    newPill: q('newpill'), emojiBtn: q('emoji-btn'), emojiPanel: q('emoji-panel'), spoilerBtn: q('spoiler-btn'),
    hint: q('hint'), replySnippet: q('reply-snippet')
  };

  /* Il popup delle regole va nel body: così nessun antenato lo taglia o lo sposta */
  if (el.rulesDialog) {
    const owner = el.rulesDialog.getAttribute('data-kc-owner');
    if (owner) {
      const old = document.querySelector('dialog[data-kc-owner="' + owner + '"]');
      if (old && old !== el.rulesDialog) old.remove();
    }
    document.body.appendChild(el.rulesDialog);
    el.rulesDialog.addEventListener('click', function (e) { if (e.target === el.rulesDialog) closeDialog(el.rulesDialog); });
  }

  /* Avatar salvato su questo dispositivo (solo indirizzi ammessi) */
  let localAvatarUrl = null;
  if (user.email) {
    try { localAvatarUrl = safeAvatarUrl(localStorage.getItem('koji_avatar_url_' + user.email)); } catch (e) { localAvatarUrl = null; }
    if (localAvatarUrl && el.inputImg && el.inputInitial) {
      el.inputImg.src = localAvatarUrl;
      el.inputImg.hidden = false;
      el.inputInitial.hidden = true;
    }
  }

  let fs = null;
  let db = null;
  let unsub = null;
  let docs = [];
  let full = false;
  let limitN = pageSize;
  let gotSnapshot = false;
  let slowTimer = null;
  let loadingMore = false;
  let rendered = false;
  let activeReplyId = null;
  let sortMode = 'recent';
  let expanded = false;
  let lastCollapsed = false;
  let sending = false;
  let started = false;
  let sheetOpen = false;
  let keepAnchor = false;
  let forceScroll = false;
  let vvHandler = null;
  let sheetClosing = false;
  let sheetOffsets = null;
  let authUid = '';
  let authPromise = null;
  const sendLog = [];
  const pendingLikes = new Set();
  const revealed = new Set();
  const cache = new Map();

  /* ----- stati ----- */
  function setState(state) {
    root.setAttribute('data-state', state);
    root.classList.toggle('is-empty', state === 'empty');
    if (el.loading) el.loading.hidden = state !== 'loading';
    if (el.empty) el.empty.hidden = state !== 'empty';
    if (el.error) el.error.hidden = state !== 'error';
    if (el.list) el.list.hidden = state !== 'ready';
    if (state === 'loading' || state === 'error') {
      if (el.badge && state === 'error') el.badge.hidden = true;
      if (el.more) el.more.hidden = true;
      if (el.expand) el.expand.hidden = true;
    }
  }

  function countLabel() {
    const n = docs.length;
    if (full) return S.tx.replace('[count]', n + '+');
    if (n === 0) return S.t0;
    if (n === 1) return S.t1;
    return S.tx.replace('[count]', n);
  }

  function updateBadge() {
    if (!el.badge || !el.badgeText) return;
    el.badge.hidden = false;
    if (!docs.length) {
      el.badgeText.textContent = S.badge_empty;
      if (el.badgeDot) el.badgeDot.classList.remove('is-live');
      return;
    }
    const newest = msOf(docs[0]);
    const diff = newest ? Date.now() - newest : 0;
    let label;
    if (!newest) label = S.just_now.toLowerCase();
    else if (diff >= 7 * 86400000) label = 'il ' + relTime(newest, S.just_now);
    else label = relTime(newest, S.just_now).toLowerCase();
    el.badgeText.textContent = S.badge_last + ' ' + label;
    if (el.badgeDot) el.badgeDot.classList.toggle('is-live', diff < 3600000);
  }

  /* barra compatta (mobile): ultimo messaggio e conteggio */
  function updatePeek() {
    if (!el.peekText) return;
    if (!docs.length) {
      el.peekText.textContent = S.peek_empty;
      if (el.peekMeta) el.peekMeta.textContent = '';
      return;
    }
    const d = docs[0];
    const who = decodeBasicEntities(d.username || '').slice(0, 24) || S.anonymous;
    let text = String(d.text == null ? '' : d.text).replace(/\s+/g, ' ').trim();
    if (text.slice(0, 9).toLowerCase() === '[spoiler]') text = S.spoiler_hidden;
    el.peekText.textContent = who + ': ' + text.slice(0, 90);
    const ts = msOf(d);
    if (el.peekMeta) el.peekMeta.textContent = countLabel() + (ts ? ' · ' + relTime(ts, S.just_now) : '');
  }

  function refreshTimes() {
    root.querySelectorAll('time[data-ts]').forEach(function (t) {
      const ts = parseInt(t.getAttribute('data-ts'), 10);
      if (ts) t.textContent = relTime(ts, S.just_now);
    });
    if (docs.length) { updateBadge(); updatePeek(); }
  }

  /* ----- Firebase ----- */
  function waitForDb(ms) {
    return new Promise(function (resolve, reject) {
      const t0 = Date.now();
      (function poll() {
        if (window.kojiDb) return resolve(window.kojiDb);
        if (Date.now() - t0 > ms) return reject(new Error('window.kojiDb non disponibile'));
        setTimeout(poll, 150);
      })();
    });
  }

  /* Accesso anonimo di Firebase: serve solo a collegare ogni messaggio al browser che lo ha scritto (per poterlo eliminare).
     Parte soltanto per chi ha fatto il login su Koji. */
  function ensureAuth() {
    if (authUid) return Promise.resolve(authUid);
    if (authPromise) return authPromise;
    authPromise = (async function () {
      try {
        const a = window.kojiAuth;
        if (!a) return '';
        const m = await import(AUTH_URL);
        if (a.authStateReady) await a.authStateReady();
        if (!a.currentUser) await m.signInAnonymously(a);
        authUid = a.currentUser ? a.currentUser.uid : '';
      } catch (e) {
        console.debug('[koji-chat] accesso anonimo non disponibile', e && e.code);
      }
      authPromise = null;
      return authUid;
    })();
    return authPromise;
  }

  async function start() {
    started = true;
    gotSnapshot = false;
    setState('loading');
    try {
      db = await waitForDb(8000);
      fs = await import(FIRESTORE_URL);
    } catch (e) {
      console.error('[koji-chat]', e);
      setState('error');
      return;
    }
    subscribe();
    if (loggedIn) ensureAuth().then(function (u) { if (u && docs.length) render(true); });
    if (cfg.trending && cfg.trending.enabled) loadTrending();
  }

  function subscribe() {
    if (unsub) { unsub(); unsub = null; }
    const rooms = [roomId].concat(legacyRooms);
    const cond = rooms.length > 1 ? fs.where('pageId', 'in', rooms) : fs.where('pageId', '==', roomId);
    const query = fs.query(fs.collection(db, 'comments'), cond, fs.orderBy('timestamp', 'desc'), fs.limit(limitN));
    clearTimeout(slowTimer);
    slowTimer = setTimeout(function () { if (!gotSnapshot) setState('error'); }, 12000);
    unsub = fs.onSnapshot(query, onSnap, function (err) {
      console.error('[koji-chat] errore lettura commenti:', err);
      if (err && err.code === 'failed-precondition' && legacyRooms.length) {
        legacyRooms.length = 0;
        return subscribe();
      }
      if (!gotSnapshot) setState('error');
    });
  }

  function onSnap(snapshot) {
    const firstTime = !gotSnapshot;
    gotSnapshot = true;
    clearTimeout(slowTimer);
    const prevNewest = docs.length ? msOf(docs[0]) : 0;
    const wasLoadingMore = loadingMore;
    loadingMore = false;
    if (el.more) el.more.removeAttribute('aria-busy');

    keepAnchor = wasLoadingMore;
    /* "in invio" solo finché manca la data confermata dal server: un mi piace in attesa non rende "in invio" il messaggio */
    docs = snapshot.docs.map(function (d) { return Object.assign({ id: d.id, _pending: d.metadata.hasPendingWrites && d.data({ serverTimestamps: 'none' }).timestamp == null }, d.data({ serverTimestamps: 'estimate' })); });
    full = snapshot.size >= limitN;

    if (!firstTime && !wasLoadingMore && el.live) {
      snapshot.docChanges().forEach(function (ch) {
        if (ch.type !== 'added' || ch.doc.metadata.hasPendingWrites) return;
        const data = ch.doc.data({ serverTimestamps: 'estimate' });
        if (String(data.userId) === userId || msOf(data) <= prevNewest) return;
        el.live.textContent = S.live_new.replace('[nome]', decodeBasicEntities(data.username || S.anonymous));
        if (el.newPill && !nearBottom()) el.newPill.hidden = false;
      });
    }

    render(wasLoadingMore);
    if (firstTime) {
      syncRoom();
      handleHash();
    }
  }

  /* ----- disegno ----- */
  function isLiked(d) {
    return loggedIn && Array.isArray(d.likedBy) && d.likedBy.indexOf(userId) !== -1;
  }

  function isMine(d) {
    return loggedIn && String(d.userId) === userId;
  }

  function messageHTML(d, isReply, cont, parentName, parentId) {
    const rawName = decodeBasicEntities(d.username || '').slice(0, 60) || S.anonymous;
    const name = escapeHtml(rawName);
    const initial = escapeHtml(rawName.charAt(0).toUpperCase());
    const id = escapeHtml(d.id);
    const mine = isMine(d);
    const staff = staffIds.indexOf(String(d.userId)) !== -1;
    const liked = isLiked(d);
    const likes = Math.max(0, parseInt(d.likesCount, 10) || 0);
    const ts = msOf(d);

    let text = String(d.text == null ? '' : d.text).slice(0, 2000);
    let spoiler = false;
    const lead = text.replace(/^\s+/, '');
    if (lead.slice(0, 9).toLowerCase() === '[spoiler]') {
      spoiler = true;
      text = lead.slice(9).replace(/^[\s:\-–]+/, '');
    }
    const isOpen = revealed.has(d.id);

    const avatarUrl = safeAvatarUrl(d.avatarUrl) || (mine && localAvatarUrl ? localAvatarUrl : null);
    const avatar = cont ? '' : avatarUrl
      ? "<div class='kc-avatar'><img src='" + escapeHtml(avatarUrl) + "' alt='' width='42' height='42' loading='lazy'></div>"
      : "<div class='kc-avatar'><span class='kc-avatar-letter'>" + initial + '</span></div>';

    const dateHTML = d._pending
      ? "<span class='kc-date kc-date--pending'>" + escapeHtml(S.sending) + '</span>'
      : ts
      ? "<time class='kc-date' data-ts='" + ts + "' datetime='" + new Date(ts).toISOString() + "' title='" + escapeHtml(fullDate(ts)) + "'>" + escapeHtml(relTime(ts, S.just_now)) + '</time>'
      : "<span class='kc-date'>" + escapeHtml(S.just_now) + '</span>';

    const textHTML = spoiler
      ? "<div class='kc-spoiler" + (isOpen ? ' is-revealed' : '') + "'>" +
        "<button type='button' class='kc-spoiler-btn' data-action='reveal' data-id='" + id + "'" + (isOpen ? ' hidden' : '') + '>' + escapeHtml(S.spoiler) + '</button>' +
        "<p class='kc-text kc-text--spoiler'" + (isOpen ? '' : " aria-hidden='true'") + '>' + escapeHtml(text) + '</p></div>'
      : "<p class='kc-text'>" + escapeHtml(text) + '</p>';

    const likeBtn =
      "<button type='button' class='kc-act kc-like" + (liked ? ' is-liked' : '') + "' data-action='like' data-id='" + id + "' aria-pressed='" + (liked ? 'true' : 'false') + "' aria-label='" + escapeHtml(S.like_aria) + "'>" +
      ICONS.heart + "<span class='kc-like-count'>" + likes + '</span></button>';

    const replyBtn = isReply ? '' :
      "<button type='button' class='kc-act kc-reply-btn' data-action='reply' data-id='" + id + "' data-user='" + name + "' data-snippet='" + escapeHtml(spoiler ? '' : text.replace(/\s+/g, ' ').slice(0, 80)) + "' aria-label='" + escapeHtml(S.btn_reply) + ' ' + name + "'>" +
      ICONS.reply + '<span>' + escapeHtml(S.btn_reply) + '</span></button>';

    const canDelete = mine && (!d.ownerUid || d.ownerUid === authUid);
    const menuItem = mine
      ? "<button type='button' role='menuitem' class='kc-menu-item kc-menu-item--danger' data-action='delete' data-id='" + id + "'>" + ICONS.trash + escapeHtml(S.btn_delete) + '</button>'
      : "<button type='button' role='menuitem' class='kc-menu-item' data-action='report' data-id='" + id + "'>" + ICONS.flag + escapeHtml(S.btn_report) + '</button>';

    const options = mine && !canDelete ? '' :
      "<div class='kc-opts'>" +
      "<button type='button' class='kc-act kc-more' data-action='menu' data-id='" + id + "' aria-haspopup='menu' aria-expanded='false' aria-label='" + escapeHtml(S.options_aria) + "'>" + ICONS.more + '</button>' +
      "<div class='kc-menu' role='menu' hidden>" + menuItem + '</div></div>';

    const metaHTML = cont
      ? "<span class='kc-vh'>" + name + '</span>'
      : "<div class='kc-meta'><span class='kc-author'>" + name + '</span>' +
        (staff ? "<span class='kc-staff'>" + escapeHtml(S.staff) + '</span>' : '') +
        "<span class='kc-spacer'></span>" + dateHTML + '</div>';

    const replyTo = isReply && !cont && parentName
      ? "<button type='button' class='kc-replyto' data-action='goto' data-id='" + escapeHtml(parentId || '') + "' aria-label='" + escapeHtml(S.in_reply_to + ' ' + parentName) + "'><svg viewBox='0 0 24 24' width='12' height='12' fill='none' stroke='currentColor' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round' aria-hidden='true'><polyline points='9 14 4 9 9 4'></polyline><path d='M20 20v-7a4 4 0 0 0-4-4H4'></path></svg> " + escapeHtml(S.in_reply_to) + ' <strong>' + escapeHtml(parentName) + '</strong></button>'
      : '';

    return "<article class='kc-item" + (isReply ? ' kc-item--reply' : '') + (cont ? ' kc-item--cont' : '') + (staff ? ' is-staff' : '') + (d._pending ? ' is-pending' : '') + "' id='kc-" + id + "'>" +
      "<div class='kc-avatar-wrap'>" + avatar + '</div>' +
      "<div class='kc-body'>" +
      metaHTML +
      replyTo +
      textHTML +
      "<div class='kc-actions'>" + likeBtn + replyBtn + options + '</div>' +
      '</div></article>';
  }

  function ghostHTML(label) {
    return "<div class='kc-item kc-ghost'><p class='kc-text'>" + escapeHtml(label) + '</p></div>';
  }

  function part(d) {
    return [d.id, d.text, d.username, d.avatarUrl || '', msOf(d), d.likesCount | 0, isLiked(d) ? 1 : 0, isMine(d) ? 1 : 0, revealed.has(d.id) ? 1 : 0, d._pending ? 1 : 0, d.ownerUid && d.ownerUid === authUid ? 1 : 0].join('\u0001');
  }

  function signature(t) {
    return [t.orphan ? 1 : 0, t.compact ? 1 : 0, full ? 1 : 0, t.root.placeholder ? t.root.id : part(t.root)].concat(t.replies.map(part)).join('\u0002');
  }

  function buildThread(t, animate) {
    const node = document.createElement('div');
    node.className = 'kc-thread' + (animate ? ' kc-enter' : '');
    node.setAttribute('data-key', t.key);
    node.innerHTML = t.root.placeholder ? ghostHTML(full ? S.ghost_old : S.ghost_deleted) : messageHTML(t.root, false, !!t.compact);
    if (t.replies.length) {
      const parentName = t.root.placeholder ? '' : decodeBasicEntities(t.root.username || '').slice(0, 40) || S.anonymous;
      const wrap = document.createElement('div');
      wrap.className = 'kc-replies';
      wrap.innerHTML = t.replies.map(function (r, i) {
        const prev = t.replies[i - 1];
        const cont = !!prev && !!r.userId && String(prev.userId) === String(r.userId) && Math.abs(msOf(r) - msOf(prev)) < 300000;
        return messageHTML(r, true, cont, parentName, t.root.id);
      }).join('');
      node.appendChild(wrap);
    }
    return node;
  }

  function reconcile(threads, animateNew) {
    const seen = new Set();
    const desired = [];
    threads.forEach(function (t) {
      seen.add(t.key);
      const sig = t.sep ? 'sep:' + t.label : signature(t);
      const rec = cache.get(t.key);
      if (rec && rec.sig === sig) { desired.push(rec.el); return; }
      if (t.sep) {
        const sepNode = document.createElement('div');
        sepNode.className = 'kc-sep';
        sepNode.setAttribute('data-key', t.key);
        sepNode.setAttribute('role', 'separator');
        sepNode.innerHTML = '<span>' + escapeHtml(t.label) + '</span>';
        if (rec && rec.el.parentNode) rec.el.replaceWith(sepNode);
        cache.set(t.key, { sig: sig, el: sepNode });
        desired.push(sepNode);
        return;
      }
      const node = buildThread(t, rec ? false : animateNew);
      if (rec) {
        const act = document.activeElement;
        let refocus = null;
        if (act && rec.el.contains(act) && act.getAttribute('data-action')) {
          refocus = '[data-action="' + act.getAttribute('data-action') + '"][data-id="' + act.getAttribute('data-id') + '"]';
        }
        if (rec.el.parentNode) rec.el.replaceWith(node);
        if (refocus) {
          const f = node.querySelector(refocus);
          if (f) f.focus({ preventScroll: true });
        }
      }
      cache.set(t.key, { sig: sig, el: node });
      desired.push(node);
    });
    cache.forEach(function (rec, key) {
      if (!seen.has(key)) { rec.el.remove(); cache.delete(key); }
    });
    desired.forEach(function (node, i) {
      if (el.list.children[i] !== node) el.list.insertBefore(node, el.list.children[i] || null);
    });
  }

  function buildThreads() {
    const rootsById = new Map();
    const rootList = [];
    const replies = new Map();
    docs.forEach(function (d) {
      const pid = typeof d.parentId === 'string' && d.parentId ? d.parentId : null;
      if (pid) {
        if (!replies.has(pid)) replies.set(pid, []);
        replies.get(pid).push(d);
      } else {
        rootList.push(d);
        rootsById.set(d.id, d);
      }
    });
    const byTimeAsc = function (a, b) { return msOf(a) - msOf(b); };
    const threads = rootList.map(function (r) {
      return { key: r.id, root: r, replies: (replies.get(r.id) || []).slice().sort(byTimeAsc), sortTs: msOf(r), likes: Math.max(0, parseInt(r.likesCount, 10) || 0) };
    });
    replies.forEach(function (arr, pid) {
      if (rootsById.has(pid)) return;
      const sorted = arr.slice().sort(byTimeAsc);
      threads.push({ key: 'o-' + pid, orphan: true, root: { id: pid, placeholder: true }, replies: sorted, sortTs: msOf(sorted[sorted.length - 1]), likes: 0 });
    });
    if (sortMode === 'top') threads.sort(function (a, b) { return (b.likes - a.likes) || (b.sortTs - a.sortTs); });
    else threads.sort(function (a, b) { return a.sortTs - b.sortTs; });
    if (sortMode !== 'top') {
      for (let i = 1; i < threads.length; i++) {
        const a = threads[i - 1];
        const b = threads[i];
        if (!a.orphan && !b.orphan && !a.replies.length && !b.replies.length && a.root.userId && String(a.root.userId) === String(b.root.userId) &&
          Math.abs(a.sortTs - b.sortTs) < 300000 && dayKey(a.sortTs) === dayKey(b.sortTs)) b.compact = true;
      }
    }
    return threads;
  }

  function nearBottom() {
    const sc = el.scroll;
    if (!sc) return true;
    if (sortMode === 'top') return sc.scrollTop < 90;
    return sc.scrollHeight - sc.scrollTop - sc.clientHeight < 90;
  }

  function scrollToEnd(smooth) {
    const sc = el.scroll;
    if (!sc) return;
    const top = sortMode === 'top' ? 0 : sc.scrollHeight;
    if (smooth && !reduceMotion && sc.scrollTo) sc.scrollTo({ top: top, behavior: 'smooth' });
    else sc.scrollTop = top;
  }

  function render(suppressAnim) {
    updateBadge();
    updatePeek();
    if (el.count) el.count.textContent = countLabel();

    if (!docs.length) {
      cache.clear();
      if (el.list) el.list.innerHTML = '';
      setState('empty');
      syncControls(0);
      return;
    }
    setState('ready');
    const threads = buildThreads();
    lastCollapsed = collapseN > 0 && stackedQuery.matches && !expanded && threads.length > collapseN;
    const visible = lastCollapsed ? threads.slice(0, collapseN) : threads;
    let entries = visible;
    if (sortMode !== 'top') {
      entries = [];
      let prevDay = null;
      visible.forEach(function (t) {
        const k = dayKey(t.sortTs);
        if (k !== prevDay) { entries.push({ sep: true, key: 'sep-' + k, label: dayLabel(t.sortTs, S) }); prevDay = k; }
        entries.push(t);
      });
    }
    const sc = el.scroll;
    const prevH = sc ? sc.scrollHeight : 0;
    const prevTop = sc ? sc.scrollTop : 0;
    const wasNear = nearBottom();
    const first = !rendered;
    reconcile(entries, rendered && !suppressAnim && !reduceMotion);
    rendered = true;
    if (sc) {
      if (first || forceScroll) { scrollToEnd(); if (window.requestAnimationFrame) requestAnimationFrame(function () { scrollToEnd(); }); }
      else if (keepAnchor && sortMode !== 'top') sc.scrollTop = prevTop + (sc.scrollHeight - prevH);
      else if (wasNear) scrollToEnd();
    }
    forceScroll = false;
    keepAnchor = false;
    syncControls(threads.length);
  }

  function syncControls(threadCount) {
    if (el.sort) el.sort.hidden = threadCount < 3;
    if (el.expand) {
      el.expand.hidden = !lastCollapsed;
      el.expand.textContent = S.expand.replace('[count]', threadCount);
    }
    if (el.more) el.more.hidden = !(full && docs.length && !lastCollapsed);
    if (el.scroll) el.scroll.classList.toggle('is-expanded', expanded);
  }

  function handleHash() {
    const hash = window.location.hash;
    if (!hash || hash.indexOf('#kc-') !== 0) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    target.classList.add('is-linked');
    target.scrollIntoView({ block: 'center', behavior: 'auto' });
  }

  /* ----- stanze (per "In tendenza") ----- */
  async function bumpRoom(delta) {
    if (!cfg.roomMeta || !roomId) return;
    try {
      const data = { handle: roomId, title: String(cfg.roomMeta.title || '').slice(0, 120), adult: !!cfg.roomMeta.adult, count: fs.increment(delta) };
      if (delta > 0) data.lastMessageAt = fs.serverTimestamp();
      await fs.setDoc(fs.doc(db, 'rooms', roomId), data, { merge: true });
    } catch (e) {
      console.debug('[koji-chat] la collezione "rooms" non è scrivibile (regole Firestore)', e && e.code);
    }
  }

  /* Se la stanza non ha ancora la sua riga, il primo utente con l'accesso la crea (serve a far comparire le collezioni già attive) */
  async function syncRoom() {
    if (!cfg.roomMeta || !loggedIn || !docs.length || !roomId) return;
    const key = 'kc_room_sync_' + roomId;
    try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1'); } catch (e) { /* ok */ }
    try {
      const ref = fs.doc(db, 'rooms', roomId);
      const snap = await fs.getDoc(ref);
      if (snap.exists()) return;
      const newest = msOf(docs[0]);
      await fs.setDoc(ref, {
        handle: roomId,
        title: String(cfg.roomMeta.title || '').slice(0, 120),
        adult: !!cfg.roomMeta.adult,
        count: docs.length,
        lastMessageAt: newest ? fs.Timestamp.fromMillis(newest) : fs.serverTimestamp()
      });
    } catch (e) {
      console.debug('[koji-chat] sincronizzazione stanza non riuscita', e && e.code);
    }
  }

  /* ----- In tendenza (solo home) ----- */
  async function checkCollection(handle) {
    try {
      const h = encodeURIComponent(handle);
      const opts = { headers: { Accept: 'application/json' } };
      const both = await Promise.all([
        fetch('/collections/' + h + '/products.json?limit=1', opts).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; }),
        fetch('/collections/' + h + '.json', opts).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; })
      ]);
      const json = both[0];
      if (!json || !json.products || !json.products.length) return null;
      const p = json.products[0];
      const col = both[1] && both[1].collection;
      /* prima la foto della collezione, se manca quella del primo prodotto */
      const colImg = col && col.image && col.image.src ? col.image.src : '';
      return { img: colImg || (p.images && p.images[0] ? p.images[0].src : '') };
    } catch (e) {
      return null;
    }
  }

  async function computeTrending(tc) {
    const exclude = new Set([roomId, ''].concat(legacyRooms, tc.exclude || []));
    /* le saghe più commentate in assoluto: classifica per numero di messaggi */
    const snap = await fs.getDocs(fs.query(fs.collection(db, 'rooms'), fs.orderBy('count', 'desc'), fs.limit(20)));
    const cands = [];
    snap.forEach(function (d) {
      const r = d.data();
      const handle = String(r.handle || d.id);
      const t = r.lastMessageAt && typeof r.lastMessageAt.toMillis === 'function' ? r.lastMessageAt.toMillis() : 0;
      if (!handle || exclude.has(d.id) || exclude.has(handle) || r.adult) return;
      if (!(typeof r.count === 'number' && r.count > 0)) return;
      cands.push({ handle: handle, title: String(r.title || '').slice(0, 80) || prettifyHandle(handle), count: typeof r.count === 'number' ? r.count : 0, t: t });
    });
    const out = [];
    for (let i = 0; i < cands.length && i < 8 && out.length < (tc.max || 3); i++) {
      const info = await checkCollection(cands[i].handle);
      if (info) out.push(Object.assign(cands[i], { img: info.img }));
    }
    return out;
  }

  async function loadTrending() {
    if (!el.trending || !el.trendingList) return;
    const tc = cfg.trending;
    const ck = 'kc_trend_v3';
    let items = null;
    try {
      const c = JSON.parse(sessionStorage.getItem(ck) || 'null');
      if (c && Date.now() - c.t < 600000) items = c.items;
    } catch (e) { items = null; }
    if (!items) {
      try { items = await computeTrending(tc); } catch (e) { console.debug('[koji-chat] tendenze non disponibili', e && e.code); return; }
      try { sessionStorage.setItem(ck, JSON.stringify({ t: Date.now(), items: items })); } catch (e) { /* ok */ }
    }
    if (!items.length) return;
    el.trendingList.innerHTML = items.map(function (it, i) {
      const imgOk = it.img && it.img.indexOf('https://cdn.shopify.com/') === 0;
      const img = imgOk ? "<img class='kc-trend-img' src='" + escapeHtml(it.img + (it.img.indexOf('?') === -1 ? '?' : '&') + 'width=96') + "' alt='' width='40' height='40' loading='lazy'>" : '';
      const meta = (it.count > 0 ? it.count + ' ' + (it.count === 1 ? S.trend_one : S.trend_other) : '') + (it.t ? (it.count > 0 ? ' · ' : '') + relTime(it.t, S.just_now) : '');
      const href = tc.hubUrl ? tc.hubUrl + '?room=' + encodeURIComponent(it.handle) : '/collections/' + encodeURIComponent(it.handle);
      return "<a class='kc-trend-item' href='" + escapeHtml(href) + "' data-rank='" + (i + 1) + "'>" +
        "<span class='kc-trend-rank'>#" + (i + 1) + '</span>' + img +
        "<span class='kc-trend-text'><span class='kc-trend-name'>" + escapeHtml(it.title) + "</span><span class='kc-trend-meta'>" + escapeHtml(meta) + '</span></span></a>';
    }).join('');
    el.trending.hidden = false;
  }

  /* ----- azioni ----- */
  function needLogin(message) {
    toast(message, { href: loginUrl, label: S.login_cta, kind: 'info' });
  }

  function closeMenus() {
    root.querySelectorAll('.kc-menu:not([hidden])').forEach(function (m) {
      m.hidden = true;
      const b = m.previousElementSibling;
      if (b) b.setAttribute('aria-expanded', 'false');
      const it = m.closest('.kc-item');
      if (it) it.classList.remove('is-menu-open');
    });
  }

  function toggleMenu(btn) {
    const menu = btn.nextElementSibling;
    if (!menu) return;
    const willOpen = menu.hidden;
    closeMenus();
    if (!willOpen) return;
    menu.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    const item = btn.closest('.kc-item');
    if (item) item.classList.add('is-menu-open');
    const first = menu.querySelector('button');
    if (first) first.focus({ preventScroll: true });
  }

  async function doLike(id) {
    const d = docs.find(function (x) { return x.id === id; });
    if (!d || pendingLikes.has(id)) return;
    pendingLikes.add(id);
    const liked = isLiked(d);
    try {
      await fs.updateDoc(fs.doc(db, 'comments', id), {
        likesCount: fs.increment(liked ? -1 : 1),
        likedBy: liked ? fs.arrayRemove(userId) : fs.arrayUnion(userId)
      });
    } catch (e) {
      console.error('[koji-chat] like non riuscito', e);
      toast(S.error_like, { kind: 'error' });
    } finally {
      pendingLikes.delete(id);
    }
  }

  async function doDelete(id) {
    const ok = await askConfirm({ title: S.delete_title, text: S.confirm_delete, ok: S.btn_delete, cancel: S.cancel, danger: true });
    if (!ok) return;
    try {
      await fs.deleteDoc(fs.doc(db, 'comments', id));
      toast(S.toast_deleted);
      bumpRoom(-1);
    } catch (e) {
      console.error('[koji-chat] eliminazione non riuscita', e);
      toast(e && e.code === 'permission-denied' ? S.error_delete_denied : S.error_delete, { kind: 'error' });
    }
  }

  async function doReport(id) {
    const key = 'kc_rep_' + id;
    try { if (sessionStorage.getItem(key)) { toast(S.toast_reported_already); return; } } catch (e) { /* ok */ }
    let sent = false;
    if (loggedIn) {
      try {
        await fs.addDoc(fs.collection(db, 'reports'), { commentId: id, pageId: roomId, reporterId: userId, timestamp: fs.serverTimestamp() });
        sent = true;
      } catch (e) {
        console.debug('[koji-chat] la collezione "reports" non è scrivibile, uso la mail', e && e.code);
      }
    }
    if (!sent) {
      /* senza accesso (o senza regole per "reports") la segnalazione parte via mail, con il link al messaggio */
      const mail = cfg.reportEmail || '';
      if (!mail) return;
      const body = 'Pagina: ' + window.location.href.split('#')[0] + '#kc-' + id + '\nID messaggio: ' + id + '\nMotivo: ';
      window.location.href = 'mailto:' + mail + '?subject=' + encodeURIComponent(S.report_subject) + '&body=' + encodeURIComponent(body);
      return;
    }
    try { sessionStorage.setItem(key, '1'); } catch (e) { /* ok */ }
    toast(S.toast_reported);
  }

  /* ----- pannello a schermo intero (mobile) ----- */
  function clipFor(rect, radius) {
    const t = Math.max(0, rect.top);
    const r = Math.max(0, window.innerWidth - rect.right);
    const b = Math.max(0, window.innerHeight - rect.bottom);
    const l = Math.max(0, rect.left);
    return 'inset(' + t + 'px ' + r + 'px ' + b + 'px ' + l + 'px round ' + radius + 'px)';
  }

  function openSheet() {
    if (sheetOpen || sheetClosing || !sheetQuery.matches) return;
    const panel = root.querySelector('.kc-panel');
    const from = panel ? panel.getBoundingClientRect() : null;
    const rootRect = root.getBoundingClientRect();
    if (from) sheetOffsets = { top: from.top - rootRect.top, left: from.left - rootRect.left, width: from.width, height: from.height };
    /* il posto in pagina resta occupato mentre la chat è a schermo intero */
    root.style.minHeight = rootRect.height + 'px';
    sheetOpen = true;
    root.classList.add('is-open');
    document.documentElement.classList.add('kc-lock');
    try { history.pushState({ kc: 1 }, ''); } catch (e) { /* ok */ }
    if (!started) start();
    const vv = window.visualViewport;
    if (vv && panel) {
      vvHandler = function () { panel.style.height = vv.height + 'px'; panel.style.top = vv.offsetTop + 'px'; };
      vv.addEventListener('resize', vvHandler);
      vv.addEventListener('scroll', vvHandler);
      vvHandler();
    }
    if (window.requestAnimationFrame) requestAnimationFrame(function () { scrollToEnd(); });
    if (panel && from && !reduceMotion && panel.animate) {
      panel.animate(
        [{ clipPath: clipFor(from, 22), opacity: 0.6 }, { clipPath: 'inset(0px 0px 0px 0px round 0px)', opacity: 1 }],
        { duration: 440, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
      );
    }
    if (el.sheetClose) el.sheetClose.focus({ preventScroll: true });
  }

  function finishClose() {
    const panel = root.querySelector('.kc-panel');
    root.classList.remove('is-open');
    document.documentElement.classList.remove('kc-lock');
    root.style.minHeight = '';
    if (panel) { panel.style.height = ''; panel.style.top = ''; }
    sheetClosing = false;
    if (el.sheetOpen) el.sheetOpen.focus({ preventScroll: true });
    if (window.requestAnimationFrame) requestAnimationFrame(function () { scrollToEnd(); });
  }

  function closeSheet(fromPop) {
    if (!sheetOpen) return;
    sheetOpen = false;
    sheetClosing = true;
    const vv = window.visualViewport;
    if (vv && vvHandler) { vv.removeEventListener('resize', vvHandler); vv.removeEventListener('scroll', vvHandler); vvHandler = null; }
    if (!fromPop && history.state && history.state.kc) { try { history.back(); } catch (e) { /* ok */ } }
    const panel = root.querySelector('.kc-panel');
    if (panel && sheetOffsets && !reduceMotion && panel.animate) {
      const rr = root.getBoundingClientRect();
      const target = { top: rr.top + sheetOffsets.top, left: rr.left + sheetOffsets.left, right: rr.left + sheetOffsets.left + sheetOffsets.width, bottom: rr.top + sheetOffsets.top + sheetOffsets.height };
      const anim = panel.animate(
        [{ clipPath: 'inset(0px 0px 0px 0px round 0px)', opacity: 1 }, { clipPath: clipFor(target, 22), opacity: 0.6 }],
        { duration: 360, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' }
      );
      anim.onfinish = function () { finishClose(); anim.cancel(); };
      anim.oncancel = function () { if (sheetClosing) finishClose(); };
    } else {
      finishClose();
    }
  }

  function closeEmoji() {
    if (!el.emojiPanel || el.emojiPanel.hidden) return;
    el.emojiPanel.hidden = true;
    if (el.emojiBtn) el.emojiBtn.setAttribute('aria-expanded', 'false');
  }

  function startReply(id, name, snippet) {
    activeReplyId = id;
    if (el.replyName) el.replyName.textContent = name;
    if (el.replySnippet) el.replySnippet.textContent = snippet || S.spoiler_reply;
    if (el.replyCtx) el.replyCtx.hidden = false;
    if (el.textarea) el.textarea.focus({ preventScroll: true });
    if (el.composer) el.composer.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
  }

  function cancelReply() {
    activeReplyId = null;
    if (el.replyCtx) el.replyCtx.hidden = true;
  }

  function gotoMessage(id) {
    const target = id ? document.getElementById('kc-' + id) : null;
    const sc = el.scroll;
    if (!target || !sc || !root.contains(target)) { toast(S.goto_missing, { kind: 'info' }); return; }
    const r = target.getBoundingClientRect();
    const sr = sc.getBoundingClientRect();
    const top = sc.scrollTop + (r.top - sr.top) - (sc.clientHeight - r.height) / 2;
    if (!reduceMotion && sc.scrollTo) sc.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    else sc.scrollTop = Math.max(0, top);
    target.classList.remove('is-flash');
    void target.offsetWidth;
    target.classList.add('is-flash');
    setTimeout(function () { target.classList.remove('is-flash'); }, 1800);
  }

  /* mobile: scorri a destra per rispondere, doppio tocco per il mi piace */
  (function touchGestures() {
    if (!el.scroll || !('ontouchstart' in window)) return;
    let st = null;
    let lastTap = { t: 0, id: '' };
    const TH = 64;
    function itemOf(t) {
      const it = t && t.closest ? t.closest('.kc-item:not(.kc-item--reply):not(.kc-ghost)') : null;
      return it && !t.closest('button,a,textarea,.kc-menu') ? it : null;
    }
    el.scroll.addEventListener('touchstart', function (e) {
      const it = itemOf(e.target);
      if (!it || e.touches.length !== 1 || (sheetQuery.matches && !sheetOpen)) { st = null; return; }
      st = { it: it, x: e.touches[0].clientX, y: e.touches[0].clientY, dx: 0, lock: false, dead: false };
    }, { passive: true });
    el.scroll.addEventListener('touchmove', function (e) {
      if (!st || st.dead) return;
      const dx = e.touches[0].clientX - st.x;
      const dy = e.touches[0].clientY - st.y;
      if (!st.lock) {
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { st.dead = true; return; }
        if (dx > 10 && Math.abs(dx) > Math.abs(dy) * 1.5) st.lock = true; else return;
      }
      st.dx = Math.max(0, Math.min(dx, 96));
      st.it.classList.add('is-swiping');
      st.it.style.setProperty('--kc-sw', st.dx * 0.8 + 'px');
      st.it.style.setProperty('--kc-swp', Math.min(1, st.dx / TH));
    }, { passive: true });
    function endTouch() {
      if (!st) return;
      const it = st.it;
      const done = st.lock && st.dx >= TH;
      const wasSwipe = st.lock;
      st = null;
      it.classList.remove('is-swiping');
      it.style.removeProperty('--kc-sw');
      it.style.removeProperty('--kc-swp');
      if (done) {
        const rb = it.querySelector('[data-action="reply"]');
        if (rb) rb.click();
        return;
      }
      if (wasSwipe) return;
      const id = it.id;
      const now = Date.now();
      if (lastTap.id === id && now - lastTap.t < 320) {
        lastTap = { t: 0, id: '' };
        const lb = it.querySelector('[data-action="like"]');
        if (lb && !lb.classList.contains('is-liked')) {
          it.classList.add('is-hearted');
          setTimeout(function () { it.classList.remove('is-hearted'); }, 700);
        }
        if (lb) lb.click();
      } else {
        lastTap = { t: now, id: id };
      }
    }
    el.scroll.addEventListener('touchend', endTouch, { passive: true });
    el.scroll.addEventListener('touchcancel', function () {
      if (!st) return;
      st.it.classList.remove('is-swiping');
      st.it.style.removeProperty('--kc-sw');
      st.it.style.removeProperty('--kc-swp');
      st = null;
    }, { passive: true });
  })();

  if (el.list) {
    root.addEventListener('click', async function (e) {
      const btn = e.target.closest('[data-action]');
      if (!btn || !root.contains(btn)) return;
      const action = btn.getAttribute('data-action');
      const id = btn.getAttribute('data-id');
      if (action === 'reveal') { revealed.add(id); render(true); return; }
      if (action === 'goto') { gotoMessage(id); return; }
      if (action === 'menu') { e.preventDefault(); toggleMenu(btn); return; }
      if (action === 'delete') { closeMenus(); doDelete(id); return; }
      if (action === 'report') { closeMenus(); doReport(id); return; }
      if (action === 'reply') {
        if (!loggedIn) { needLogin(S.alert_login_reply); return; }
        startReply(id, btn.getAttribute('data-user'), btn.getAttribute('data-snippet'));
        return;
      }
      if (action === 'like') {
        if (!loggedIn) { needLogin(S.alert_login_like); return; }
        doLike(id);
      }
    });
  }

  /* ricorda dove si trovava l'utente prima di andare ad accedere */
  document.addEventListener('click', function (e) {
    const a = e.target.closest ? e.target.closest('a.kc-guest, .kc-toast a') : null;
    if (a) {
      try { sessionStorage.setItem('kc_return', JSON.stringify({ p: window.location.pathname, t: Date.now() })); } catch (err) { /* ok */ }
    }
  });
  if (loggedIn) {
    try {
      const ret = JSON.parse(sessionStorage.getItem('kc_return') || 'null');
      if (ret && ret.p === window.location.pathname && Date.now() - ret.t < 1800000) {
        sessionStorage.removeItem('kc_return');
        setTimeout(function () {
          const top = root.getBoundingClientRect().top + window.scrollY - 90;
          window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
          toast(S.logged_back, { kind: 'info' });
          if (el.textarea && !sheetQuery.matches) el.textarea.focus({ preventScroll: true });
        }, 700);
      }
    } catch (err) { /* ok */ }
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest('.kc-opts')) closeMenus();
    if (!e.target.closest('.kc-emoji-wrap')) closeEmoji();
  });
  window.addEventListener('popstate', function () { if (sheetOpen) closeSheet(true); });
  if (sheetQuery.addEventListener) sheetQuery.addEventListener('change', function () { if (!sheetQuery.matches && sheetOpen) closeSheet(); });
  if (el.sheetOpen) el.sheetOpen.addEventListener('click', openSheet);
  /* mobile: la conversazione si scorre solo a schermo intero; un tocco sulla chat in linea la apre */
  if (el.feed) {
    el.feed.addEventListener('click', function (e) {
      if (!sheetQuery.matches || sheetOpen || sheetClosing) return;
      if (e.target.closest('button, a, textarea, [data-action], .kc-opts')) return;
      openSheet();
    });
  }
  if (el.sheetClose) el.sheetClose.addEventListener('click', function () { closeSheet(); });
  if (el.newPill && el.scroll) {
    el.newPill.addEventListener('click', function () {
      scrollToEnd(true);
      el.newPill.hidden = true;
    });
    el.scroll.addEventListener('scroll', function () { if (!el.newPill.hidden && nearBottom()) el.newPill.hidden = true; }, { passive: true });
  }
  root.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      if (el.emojiPanel && !el.emojiPanel.hidden) { closeEmoji(); if (el.emojiBtn) el.emojiBtn.focus(); return; }
      const open = root.querySelector('.kc-menu:not([hidden])');
      if (open) {
        const trigger = open.previousElementSibling;
        closeMenus();
        if (trigger) trigger.focus();
        return;
      }
      if (activeReplyId && e.target === el.textarea) { cancelReply(); return; }
      if (sheetOpen) closeSheet();
    }
  });

  if (el.rulesBtn && el.rulesDialog) el.rulesBtn.addEventListener('click', function () { openDialog(el.rulesDialog); });
  if (el.replyCancel) el.replyCancel.addEventListener('click', function () { cancelReply(); if (el.textarea) el.textarea.focus(); });
  if (el.retry) el.retry.addEventListener('click', function () { start(); });
  if (el.more) {
    el.more.addEventListener('click', function () {
      limitN += pageSize;
      loadingMore = true;
      el.more.setAttribute('aria-busy', 'true');
      subscribe();
    });
  }
  if (el.expand) el.expand.addEventListener('click', function () { expanded = true; render(true); });
  if (el.sort) {
    el.sort.querySelectorAll('[data-sort]').forEach(function (b) {
      b.addEventListener('click', function () {
        sortMode = b.getAttribute('data-sort');
        forceScroll = true;
        el.sort.querySelectorAll('[data-sort]').forEach(function (x) { x.setAttribute('aria-pressed', x === b ? 'true' : 'false'); });
        render(true);
      });
    });
  }
  if (stackedQuery.addEventListener) stackedQuery.addEventListener('change', function () { if (docs.length) render(true); });

  /* ----- scrittura ----- */
  function isRateLimited() {
    const now = Date.now();
    while (sendLog.length && now - sendLog[0] > 60000) sendLog.shift();
    if (sendLog.length && now - sendLog[sendLog.length - 1] < SEND_MIN_GAP_MS) return true;
    return sendLog.length >= SEND_MAX_PER_MIN;
  }

  function updateComposer() {
    if (!el.textarea) return;
    el.textarea.style.height = 'auto';
    el.textarea.style.height = el.textarea.scrollHeight + 'px';
    const len = el.textarea.value.length;
    if (el.counter) {
      el.counter.hidden = len < MAX_LEN * 0.8;
      el.counter.textContent = len + ' / ' + MAX_LEN;
      el.counter.classList.toggle('is-warn', len > MAX_LEN * 0.9 && len <= MAX_LEN);
      el.counter.classList.toggle('is-over', len > MAX_LEN);
    }
    const linkBad = !isStaffMe && len > 0 && hasLink(cleanMessage(el.textarea.value));
    if (el.hint) {
      el.hint.hidden = !linkBad;
      if (linkBad) el.hint.textContent = S.no_links;
    }
    if (el.spoilerBtn) el.spoilerBtn.setAttribute('aria-pressed', /^\s*\[spoiler\]/i.test(el.textarea.value) ? 'true' : 'false');
    if (el.submit) el.submit.disabled = sending || len === 0 || len > MAX_LEN || linkBad;
  }

  async function send() {
    if (sending || !el.textarea) return;
    const text = cleanMessage(el.textarea.value);
    if (!text || text.length > MAX_LEN) return;
    if (!isStaffMe && hasLink(text)) { toast(S.no_links, { kind: 'error' }); return; }
    if (isRateLimited()) { toast(S.rate_limit, { kind: 'error' }); return; }
    sending = true;
    sendLog.push(Date.now());
    if (el.submit) { el.submit.disabled = true; el.submit.setAttribute('aria-busy', 'true'); }
    const replyId = activeReplyId;
    const replyLabel = el.replyName ? el.replyName.textContent : '';
    const replySnip = el.replySnippet ? el.replySnippet.textContent : '';
    /* il messaggio compare subito (stato "Invio…") e il campo si svuota: se qualcosa va storto il testo torna al suo posto */
    el.textarea.value = '';
    cancelReply();
    forceScroll = true;
    scrollToEnd();
    try {
      const uid = await ensureAuth();
      const payload = {
        pageId: roomId,
        text: text,
        userId: userId,
        username: String(user.name || '').slice(0, 40),
        avatarUrl: localAvatarUrl || null,
        timestamp: fs.serverTimestamp(),
        parentId: replyId,
        likesCount: 0,
        likedBy: []
      };
      if (uid) payload.ownerUid = uid;
      await fs.addDoc(fs.collection(db, 'comments'), payload);
      bumpRoom(1);
    } catch (e) {
      console.error('[koji-chat] invio non riuscito', e);
      el.textarea.value = el.textarea.value ? text + '\n' + el.textarea.value : text;
      if (replyId && !activeReplyId) startReply(replyId, replyLabel, replySnip);
      sendLog.pop();
      toast(S.error_send, { kind: 'error', action: { label: S.retry, fn: function () { updateComposer(); send(); } } });
    } finally {
      sending = false;
      if (el.submit) el.submit.removeAttribute('aria-busy');
      updateComposer();
    }
  }

  if (el.textarea && el.submit) {
    el.textarea.addEventListener('input', updateComposer);
    el.textarea.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); send(); }
    });
    el.submit.addEventListener('click', send);
    updateComposer();
  }
  function insertAtCursor(text) {
    const ta = el.textarea;
    const start = ta.selectionStart == null ? ta.value.length : ta.selectionStart;
    const end = ta.selectionEnd == null ? ta.value.length : ta.selectionEnd;
    ta.value = ta.value.slice(0, start) + text + ta.value.slice(end);
    const pos = start + text.length;
    ta.setSelectionRange(pos, pos);
    updateComposer();
    ta.focus();
  }

  if (el.textarea && el.emojiBtn && el.emojiPanel) {
    el.emojiPanel.innerHTML = EMOJIS.map(function (em) { return "<button type='button' class='kc-emoji' data-emoji='" + em + "'>" + em + '</button>'; }).join('');
    el.emojiBtn.addEventListener('click', function () {
      const willOpen = el.emojiPanel.hidden;
      el.emojiPanel.hidden = !willOpen;
      el.emojiBtn.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
    });
    el.emojiPanel.addEventListener('click', function (e) {
      const b = e.target.closest('[data-emoji]');
      if (b) insertAtCursor(b.getAttribute('data-emoji'));
    });
  }
  if (el.textarea && el.spoilerBtn) {
    el.spoilerBtn.addEventListener('click', function () {
      const v = el.textarea.value;
      const m = /^\s*\[spoiler\]\s*/i.exec(v);
      el.textarea.value = m ? v.slice(m[0].length) : '[SPOILER] ' + v;
      updateComposer();
      el.textarea.focus();
    });
  }
  root.querySelectorAll('[data-prompt]').forEach(function (chip) {
    chip.addEventListener('click', function () {
      if (!el.textarea) return;
      el.textarea.value = chip.getAttribute('data-prompt');
      updateComposer();
      el.textarea.focus();
      el.textarea.setSelectionRange(el.textarea.value.length, el.textarea.value.length);
    });
  });

  /* ----- pagina chat: cambio stanza senza ricaricare ----- */
  function switchRoom(id, meta) {
    id = String(id || '');
    if (!id) return;
    const m = meta || {};
    const sameRoom = id === roomId && started;
    roomId = id;
    legacyRooms = id === homeRoom && cfg.legacyRooms ? cfg.legacyRooms.map(String) : [];
    cfg.roomMeta = m.noMeta ? null : { title: String(m.title || prettifyHandle(id)), adult: !!m.adult };
    const t = root.querySelector('.kc-title');
    const st = root.querySelector('.kc-subtitle');
    if (t && m.title) t.textContent = m.title;
    if (st && m.subtitle != null) st.textContent = m.subtitle;
    if (sameRoom) return;
    if (unsub) { unsub(); unsub = null; }
    clearTimeout(slowTimer);
    docs = []; full = false; limitN = pageSize; gotSnapshot = false; rendered = false; expanded = false;
    cache.clear();
    if (el.list) el.list.innerHTML = '';
    cancelReply();
    closeMenus();
    if (el.newPill) el.newPill.hidden = true;
    if (el.badge) el.badge.hidden = true;
    if (el.count) el.count.textContent = '';
    setState('loading');
    if (started) subscribe(); else start();
  }

  root.__kc = { switchRoom: switchRoom, room: function () { return roomId; }, openRules: function () { if (el.rulesDialog) openDialog(el.rulesDialog); } };
  try { root.dispatchEvent(new CustomEvent('kc:ready')); } catch (e) { /* ok */ }

  /* ----- avvio pigro ----- */
  function lazyStart() {
    if (!('IntersectionObserver' in window)) { start(); return; }
    const io = new IntersectionObserver(function (entries) {
      if (entries.some(function (en) { return en.isIntersecting; })) { io.disconnect(); start(); }
    }, { rootMargin: '600px 0px' });
    io.observe(root);
  }

  setState('loading');
  if (!hub) lazyStart();
  setInterval(function () { if (started && !document.hidden) refreshTimes(); }, 60000);
}

function boot(scope) {
  (scope || document).querySelectorAll('[data-koji-chat]').forEach(function (root) {
    if (root.getAttribute('data-kc-ready')) return;
    root.setAttribute('data-kc-ready', '1');
    try { initChat(root); } catch (e) { console.error('[koji-chat] ' + e); }
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
else boot();
document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
